import React from "react";
import {
  DEFAULT_SEND_TIME,
  describeSendSchedule as describeSendOptions,
  isValidSendWindow,
} from "@pollyslack/hub/src/services/dmCampaign/sendTime";

/**
 * When in each recipient's day the DM goes out (PS-5765): one time, or
 * spread across a window so a big list doesn't hit an org all at once.
 */
export interface SendSchedule {
  mode: "time" | "window";
  time: string;
  windowStart: string;
  windowEnd: string;
}

export const DEFAULT_SEND_SCHEDULE: SendSchedule = {
  mode: "time",
  time: DEFAULT_SEND_TIME,
  windowStart: "10:00",
  windowEnd: "15:00",
};

/** Body fields for the campaign and sequence endpoints */
export function sendScheduleParams(schedule: SendSchedule): {
  sendTime?: string;
  sendWindow?: { start: string; end: string };
} {
  return schedule.mode === "window"
    ? { sendWindow: { start: schedule.windowStart, end: schedule.windowEnd } }
    : { sendTime: schedule.time };
}

/** "2:30 PM" or "spread from 10:00 AM to 3:00 PM", worded as the server does */
export function describeSendSchedule(schedule: SendSchedule): string {
  return describeSendOptions(sendScheduleParams(schedule));
}

/** Empty when valid; otherwise what to fix */
export function sendScheduleError(schedule: SendSchedule): string {
  if (
    schedule.mode === "window" &&
    !isValidSendWindow({ start: schedule.windowStart, end: schedule.windowEnd })
  ) {
    return "The send window has to end after it starts.";
  }
  return "";
}

interface SendTimeInputProps {
  id: string;
  value: SendSchedule;
  onChange: (value: SendSchedule) => void;
}

/**
 * The send job runs every 30 minutes (DM_CAMPAIGN_MESSAGES_CRON_JOB_FREQUENCY_MINUTES),
 * so the pickers step in half hours and a window is split into 30-minute
 * slots.
 */
export const SendTimeInput: React.FC<SendTimeInputProps> = ({
  id,
  value,
  onChange,
}) => {
  const set = (patch: Partial<SendSchedule>) =>
    onChange({ ...value, ...patch });
  const timeField = (
    fieldId: string,
    label: string,
    fieldValue: string,
    key: "time" | "windowStart" | "windowEnd",
  ) => (
    <div>
      <label htmlFor={fieldId} className="mb-1 small">
        {label}
      </label>
      <input
        id={fieldId}
        type="time"
        className="form-control"
        step={1800}
        value={fieldValue}
        // Clearing a field falls back to its default rather than sending ""
        onChange={(e) =>
          set({ [key]: e.target.value || DEFAULT_SEND_SCHEDULE[key] })
        }
      />
    </div>
  );

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-2">
        <span className="mr-3">Send Time</span>
        <div
          className="btn-group btn-group-sm"
          role="group"
          aria-label="Send time mode"
        >
          <button
            type="button"
            id={`${id}-mode-time`}
            className={`btn ${
              value.mode === "time" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => set({ mode: "time" })}
          >
            One time
          </button>
          <button
            type="button"
            id={`${id}-mode-window`}
            className={`btn ${
              value.mode === "window" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => set({ mode: "window" })}
          >
            Spread across a window
          </button>
        </div>
      </div>
      {value.mode === "time" ? (
        <div style={{ maxWidth: 200 }}>
          {timeField(id, "At", value.time, "time")}
        </div>
      ) : (
        <div className="form-row" style={{ maxWidth: 420 }}>
          <div className="col">
            {timeField(`${id}-start`, "From", value.windowStart, "windowStart")}
          </div>
          <div className="col">
            {timeField(`${id}-end`, "To", value.windowEnd, "windowEnd")}
          </div>
        </div>
      )}
      {sendScheduleError(value) && (
        <small className="text-danger d-block mt-1">
          {sendScheduleError(value)}
        </small>
      )}
    </div>
  );
};
